import React, { useEffect, useState } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';

import { useUiComponentsInternationalContext } from '@rsc/static-translations';
import ProductCard from '@rsc/ui-components/tailwind/ProductCard';
import { Button } from '@rsc/ui-components/shadcn/Button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from '@rsc/ui-components/shadcn/Carousel';

import type {
  SanityDeptCta,
  SanityGlobalProduct,
} from '@rsc/models/gatsby-graphql-types';
import CTAButton from '@rsc/ui-components/tailwind/CTAButton';

interface Props {
  block: {
    headline: string;
    copy: string;
    products: SanityGlobalProduct[];
    cta: SanityDeptCta;
  };
}

function TextAndImageCarousel({ block }: Readonly<Props>) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState<number>(0);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(false);
  const { tt } = useUiComponentsInternationalContext();

  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
      setPrevBtnDisabled(!api.canScrollPrev());
      setNextBtnDisabled(!api.canScrollNext());
    });
  }, [api]);

  if (!block) return null;

  return (
    <div className="w-full mx-0 lg:pl-20 md:pl-10 2xl:pr-0 py-14 lg:py-20">
      <div className="grid grid-cols-6 lg:grid-cols-12 items-center gap-4 md:gap-6">
        <div className="col-span-6 lg:col-span-4">
          <h2 className="text-4xl font-black mb-2">{block.headline}</h2>
          <p className="mb-6">{block.copy}</p>
          {block.cta && (
            <CTAButton
              className="hidden md:inline-flex mb-6"
              cta={block.cta}
              variant="primary"
            />
          )}
        </div>
        <div className="col-span-6 lg:col-span-8">
          <Carousel
            setApi={setApi}
            opts={{ align: 'start', dragFree: true }}
            className="flex flex-col items-start gap-3 self-stretch"
          >
            <CarouselContent className="flex pt-0 md:pr-4 md:pb-[0.899px] xl:pr-[17.333px] xl:pb-[0.788px] pl-0">
              {block?.products?.filter(Boolean).map(product => (
                <CarouselItem
                  key={product._id}
                  className="flex flex-col items-start max-w-[242px] xl:max-w-[269.111px] pl-4 md:basis-1/2"
                >
                  <ProductCard product={product} />
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex items-center gap-2 pl-1">
              <Button
                className="w-8 h-8 rounded-full"
                onClick={() => api?.scrollTo(current - 1)}
                disabled={prevBtnDisabled}
                aria-label={tt(
                  s => s.blocksDeptComponents.previousButtonAriaLabel
                )}
              >
                <MdArrowBack size="24px" className="shrink-0" />
              </Button>
              <Button
                className="w-8 h-8 rounded-full"
                onClick={() => api?.scrollTo(current + 1)}
                disabled={nextBtnDisabled}
                aria-label={tt(s => s.blocksDeptComponents.nextButtonAriaLabel)}
              >
                <MdArrowForward size="24px" className="shrink-0" />
              </Button>
            </div>
          </Carousel>
        </div>
      </div>
    </div>
  );
}

export default TextAndImageCarousel;
